import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { FormControl } from '@mui/material';
import Button from '@mui/material/Button';
import { TextField, Typography } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { SharedContainer } from '../../components/shared-container/container';
import { SharedCard } from '../../components/shared-card/card';
import OverlayLoader from '../../components/overlayLoader';
import Navbar from '../../components/shared-menu/Navbar';
import { useAuth } from '../../services/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import userKYCService from '../../services/api/kyc';
import { useTranslation } from 'react-i18next';
import { getBankVerificationError } from '../../services/api/bankVerificationError';

export default function BankAccountInfo() {
  const { t } = useTranslation();
  const { setIsAuthenticated, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ifsc, setIfsc] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  // A failed verification stays on screen (unlike the 3s snackbar) so the
  // reference ID can be read out or copied to support.
  const [bankError, setBankError] = useState(null);

  const [formErrors, setFormErrors] = useState({
    accountNumber: '',
    ifsc: '',
    bankName: '',
  });

  const isSubmitEnabled = () => {
    const hasRequiredFields =
      accountNumber?.trim() &&
      ifsc?.trim() &&
      bankName?.trim();

    const hasNoErrors =
      !formErrors.accountNumber &&
      !formErrors.ifsc &&
      !formErrors.bankName;

    return hasRequiredFields && hasNoErrors && !loading;
  };

  const getIfscError = (code) => {
    if (code.length !== 11) {
      return 'awaiting_account_details.errors.ifsc_code_length';
    }

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(code)) {
      return 'awaiting_account_details.errors.ifsc_code_invalid';
    }
  };

  const handleIFSCInput = (e) => {
    const value = e.target.value.toUpperCase(); // Convert input to uppercase
    setIfsc(value);
    setFormErrors((prev) => ({
      ...prev,
      ifsc: getIfscError(value),
    }));
  };

  const validateForm = () => {
    let valid = true;
    let errors = {};

    // Validate each field
    if (!/^\d{9,18}$/.test(accountNumber)) {
      errors.accountNumber = 'awaiting_account_details.errors.account_number_invalid';
      valid = false;
    }

    errors.ifsc = getIfscError(ifsc);
    if (errors.ifsc) {
      valid = false;
    }

    if (bankName.length < 4) {
      errors.bankName = 'awaiting_account_details.errors.bank_name_length';
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleBankSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      setBankError(null);
      // Submit the form here
      let data = {
        "account_number": accountNumber,
        "ifsc": ifsc,
        "bank_name": bankName,
      }
      userKYCService.verifyBankDetails(data, setIsAuthenticated, navigate)
        .then((response) => {
          setLoading(false);
          if (response?.success) {
            setShowSnackbar(true);
            setSnackBarMessage(response.message);
            setSnackSeverity('success');
            window.scq('Complete registration', 'pre_defined');
            // The backend has moved onboarding_status on (awaiting_esign,
            // awaiting_approval or approved). Reload the user so ProtectedRoute
            // routes on the new status; navigating with the stale one bounced back here.
            refreshUserData();
          } else {
            setBankError(getBankVerificationError(response));
          }
        })
        .catch((err) => {
          console.error('Bank verification failed:', err);
          setBankError(getBankVerificationError(err));
          setLoading(false);
        })
    } else {
      setLoading(false);
      setSnackSeverity('info');
      setShowSnackbar(true);
      setSnackBarMessage('awaiting_account_details.errors.failed_verification');
    }
  };

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('');

  const handleClose = () => {
    setShowSnackbar(false);
    setSnackBarMessage('');
    setSnackSeverity('');
  };

  useEffect(() => {
    const findBankFromIfsc = (code) => {
      userKYCService.getBankNamefromIFSC(code)
        .then((response) => {
          if (response.data) {
            setBankName(response.data['BANK']);
          }
        })
        .catch((err) => {
          console.log(err);
          setBankName('');
        });
    };

    if (!getIfscError(ifsc)) {
      findBankFromIfsc(ifsc);
    }
  }, [ifsc]);

  return (
      <>
        <Snackbar open={showSnackbar} autoHideDuration={3000} onClose={handleClose}>
          <Alert onClose={handleClose} severity={snackSeverity} sx={{ width: '100%' }}>
            <Typography>{t(snackBarMessage)}</Typography>
          </Alert>
        </Snackbar>
        {loading && <OverlayLoader open={loading} text="" />}
        <Navbar isTransparent />
        <SharedContainer className="custom-class" direction="row" justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
          <SharedCard sx={{ width: '800px' }}>

            <Box
              component="form"
              onSubmit={handleBankSubmit}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Typography variant="h4">
                {t('awaiting_account_details.header')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }} fullWidth>
                    <Typography sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '35%' }}>
                      {t('awaiting_account_details.account_number')}
                    </Typography>
                    <TextField
                      type="text"
                      id="account_number"
                      name="account_number"
                      sx={{ width: '65%' }}
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      error={!!formErrors.accountNumber}
                      helperText={t(formErrors.accountNumber)}
                      required
                    />
                  </Box>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Typography sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '35%' }}>
                      {t('awaiting_account_details.ifsc_code')}
                    </Typography>
                    <TextField
                      variant="outlined"
                      value={ifsc}
                      onChange={handleIFSCInput}
                      error={!!formErrors.ifsc}
                      helperText={t(formErrors.ifsc)}
                      required
                      sx={{ width: '65%' }}
                    />
                  </Box>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }} fullWidth>
                    <Typography sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '35%' }}>
                      {t('awaiting_account_details.bank_name')}
                    </Typography>
                    <TextField
                      type="text"
                      id="bank_name"
                      name="bank_name"
                      sx={{ width: '65%' }}
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      error={!!formErrors.bankName}
                      helperText={t(formErrors.bankName)}
                      required
                    />
                  </Box>
                </FormControl>
              </Box>

              {bankError && (
                <Alert severity="error" onClose={() => setBankError(null)}>
                  <Typography>
                    {t(bankError.messageKey, {
                      defaultValue: bankError.fallbackMessage
                        || t('awaiting_account_details.errors.verification_failed'),
                    })}
                  </Typography>
                  {bankError.requestId && (
                    <Typography variant="body2" sx={{ mt: 1, userSelect: 'all', wordBreak: 'break-all' }}>
                      {t('awaiting_account_details.errors.reference_id', { id: bankError.requestId })}
                    </Typography>
                  )}
                </Alert>
              )}

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isSubmitEnabled()}
                  fullWidth
                >
                  {t('Submit')}
                </Button>
              </Box>
            </Box>
          </SharedCard>
        </SharedContainer>
  
      </>
    );
}
